<template>
  <div
    ref="productBox"
    class="product-box"
    :style="cssVars"
    :class="[
      layoutClasses,
      {'is-narrow': isNarrow},
      { 'has-bg': backgroundColor || backgroundClass },
      backgroundColor ? '' : backgroundClass,
    ]"
  >
    <ContentGeneratorElementsMedia
      :aspect-ratio-mobile="aspectRatioMobile"
      :aspect-ratio="aspectRatio"
      :value="content?.media"
    />
    <div class="position" :class="textColor ? '' : textColorClass" :style="textColor ? `color: ${textColor}` : ''">
      <rating-average
        v-if="getStarsAverage"
        :average="getStarsAverage"
        :count="getStarsCount"
        class="category-intro-rating"
      />
      <ContentGeneratorElementsText :content="content.text" />
    </div>
    <div v-if="getButtonName && getButtonUrl" class="link">
      <a :href="getButtonUrl">
        <p>{{ getButtonName }}</p>
      </a>
    </div>
  </div>
</template>

<script>
import isMobile from '~/marketplace-front-core/mixin/isMobile.vue'
import ContentGeneratorElementsMedia from '../elements/Media.vue'
import ContentGeneratorElementsText from '../elements/Text.vue'
import { contentFindContent, contentFindOption } from '../../../plugins/content'
import RatingAverage from '../../contentEditorSeller/elements/RatingAverage'
export default {
  components: {
    ContentGeneratorElementsMedia,
    ContentGeneratorElementsText,
    RatingAverage
  },
  mixins: [isMobile],
  props: {
    value: { type: Object, default: null }
  },
  data () {
    return {
      isNarrow: false,
      resizeObserver: null
    }
  },
  computed: {
    content () {
      return {
        text: contentFindContent(this.value?.content, 'text'),
        media: contentFindContent(this.value?.content, 'media'),
        stars: contentFindContent(this.value?.content, 'stars'),
        button: contentFindContent(this.value?.content, 'button'),
        url: contentFindContent(this.value?.content, 'url')
      }
    },
    backgroundColor () {
      return contentFindOption(this.value.options, 'backgroundColor').value || null
    },
    backgroundClass () {
      return contentFindOption(this.value.options, 'backgroundClass').value || null
    },
    textColor () {
      return contentFindOption(this.value.options, 'textColor').value || null
    },
    textColorClass () {
      return contentFindOption(this.value.options, 'textColorClass').value || null
    },
    textPosition () {
      return contentFindOption(this.value.options, 'textPosition').value || 'top-left'
    },
    mobileTextPosition () {
      return contentFindOption(this.value.options, 'mobileTextPosition').value || 'text-top'
    },
    contentPosition () {
      return contentFindOption(this.value.options, 'contentPosition').value || 'content-in-picture'
    },
    mobileContentPosition () {
      return contentFindOption(this.value.options, 'mobileContentPosition').value || 'content-in-picture'
    },
    layoutClasses () {
      return this.isMobile
        ? [this.mobileTextPosition, this.mobileContentPosition]
        : [this.textPosition, this.contentPosition]
    },
    aspectRatio () {
      return contentFindOption(this.value.options, 'aspectRatio').value
    },
    aspectRatioMobile () {
      return contentFindOption(this.value.options, 'aspectRatioMobile').value
    },
    getStarsAverage () {
      return contentFindContent(this.content.stars.content, 'average').value || null
    },

    getStarsCount () {
      return contentFindContent(this.content.stars.content, 'count').value || null
    },

    getButtonUrl () {
      return contentFindContent(this.content.button.content, 'url').value || null
    },

    getButtonName () {
      return contentFindContent(this.content.button.content, 'name').value || null
    },
    cssVars () {
      return {
        'background-color': this.backgroundColor,
        '--aspectRatio': this.aspectRatio,
        '--aspectRatioMobile': this.aspectRatioMobile
      }
    }
  },
  mounted () {
    this.observeResize()
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  methods: {
    observeResize () {
      this.resizeObserver = new ResizeObserver((entries) => {
        requestAnimationFrame(() => {
          for (const entry of entries) {
            const width = entry.contentRect.width
            this.isNarrow = width < 768
          }
        })
      })
      this.resizeObserver.observe(this.$refs.productBox)
    }
  }
}
</script>

<style lang="scss" scoped>

.product-box {
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  .link {
    width: 100%;
    position: absolute;
    bottom: 40px;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }

  .position {
    position: absolute;
    width: 100%;
    max-width: 40%;
    padding: 24px 32px;

    @include mobile {
      max-width: 100%;
      padding: 32px 24px;
    }
  }

  &.top-left {
    justify-content: flex-start;
  }

  &.top-right {
    align-items: flex-end;
    justify-content: flex-start;
  }

  &.bottom-left {
    justify-content: flex-end;
  }

  &.bottom-right {
    align-items: flex-end;
    justify-content: flex-end;
  }

  &.top-right,
  &.bottom-right {
    .position {
      @include mobile {
        align-self: flex-start;
        justify-content: flex-start;
      }
    }

    :deep(.stars) {
      display: flex;
      align-self: flex-end;
      justify-content: flex-end;

      @include mobile {
        align-self: flex-start;
        justify-content: flex-start;
      }
    }

    :deep(.text-container) {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      max-width: 100%;

      @include mobile {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      .text {
        display: block;
        flex-direction: column;
        justify-content: flex-end;
        align-self: flex-end;

        @include mobile {
          align-self: flex-start;
          padding: 0;
          max-width: 100%;
        }
      }
    }
  }

  :deep(.photo-wrapper) {
    position: relative;
    width: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    background-size: cover;
    border-radius: 20px;
    aspect-ratio: var(--aspectRatio);

    :deep(img) {
      aspect-ratio: var(--aspectRatio);
    }

    :deep(.video) {
      border-radius: 20px;
    }
  }

  a {
    padding: 8px 16px;
    background-color: $primary;
    border-radius: 25px;
    display: inline-block;
    cursor: pointer;
    text-align: center;

    p {
      font-size: 16px;
      padding-right: 24px;
      color: $white;
      position: relative;
      display: inline;

      &::after {
        content: "";
        position: absolute;
        background-image: url("/core/editor/arrowBox.svg");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        top: 5px;
        right: 0;
        width: 14px;
        height: 14px;
      }
    }
  }

  :deep(.text-container) {
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    z-index: 10;

    .text {
      h1, h2, h3, h4, h5{
        font-size: 24px !important;
        margin: 0 0 12px 0;
      }
      p:last-child {
        margin: 0;
      }

      @include mobile {
        max-width: 100%;
        margin-top: 0;
      }
       & > *:last-child {
         margin-bottom: 0 !important;
       }
    }

    @include mobile {
      max-width: 100%;
      display: inline-block;
      border-bottom-left-radius: 40px;
      border-bottom-right-radius: 40px;
    }
  }
  &.text-bottom{
    justify-content: flex-end;
  }

  &.content-up,
  &.content-down {
    .link {
      position: static;
      margin-bottom: 24px;
    }
  }

  &.content-up {
    padding: 0;

    .position {
      padding: 24px 32px;
      position: relative;
    }
    &.has-bg{
      :deep(.photo-wrapper){
      order: 2;
      figure,
      img, video, .video, .placeholder-wrapper {
        border-top-left-radius: unset !important;
        border-top-right-radius: unset !important;
      }
    }
    }
    :deep(.photo-wrapper){
      order: 2;
    }

  }

  &.content-down {
    padding: 0;

    .position {
      padding: 24px 32px;
      position: relative;
    }
    &.has-bg{
    :deep(.photo-wrapper) {
      border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;
      figure,
      img, .video, video, .placeholder-wrapper {
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;
      }
    }
    }

  }

  &.is-narrow {
    padding: 0;

    .position {
      max-width: 100%;
      width: 100%;
      flex-direction: column;
      padding: 24px 32px;
      :deep(.text-container){
        width: 100%;
        .text{
          width: 100%;
        }
      }
    }
    .link{
      bottom: 24px;
    }
  }
}
.product-box.content-in-picture:has(.link) {
  :deep(.position) {
    padding-bottom: 80px;
  }
}
.product-box.content-in-picture{
  aspect-ratio: var(--aspectRatio);
  @include mobile{
     aspect-ratio: var(--aspectRatioMobile);
  }
}

</style>
